// Browser uploads: init, authenticated streamed PUT parts, complete/abort.
// S3 credentials and presigned upload URLs never reach the browser.
import { apiFetch, ApiError } from "./client";
import type { RecordingVisibility } from "../types";

/** Ответ POST /uploads/init. */
export interface UploadInitResponse {
  recording_id: string;
  upload_id: string;
  /** Размер одной части в байтах (S3 минимум 5MB кроме последней). */
  part_size: number;
  /** Максимальное число частей (S3 hard-limit 10000). */
  max_parts: number;
}

/** Описание одной завершённой части для CompleteMultipartUpload. */
export interface UploadCompletedPart {
  part_number: number;
  etag: string;
}

/** POST /uploads/init — создаёт recording + S3 multipart upload.
 *  summaryProfile — профиль AI-саммари (sales/tech/meetup/interview); пустой
 *  → бэк подставит дефолт. */
export async function initUpload(
  filename: string,
  sizeBytes: number,
  mimeType: string,
  summaryProfile?: string,
  visibility?: RecordingVisibility,
): Promise<UploadInitResponse> {
  const res = await apiFetch("/v1/uploads/init", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filename,
      size_bytes: sizeBytes,
      mime_type: mimeType,
      ...(summaryProfile ? { summary_profile: summaryProfile } : {}),
      ...(visibility ? { visibility } : {}),
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, `POST /uploads/init ${res.status}: ${text}`);
  }
  return (await res.json()) as UploadInitResponse;
}

/** POST /uploads/{id}/complete — финализация multipart. */
export async function completeUpload(
  recordingId: string,
  parts: UploadCompletedPart[],
): Promise<void> {
  const res = await apiFetch(
    `/v1/uploads/${encodeURIComponent(recordingId)}/complete`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parts }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(
      res.status,
      `POST /uploads/${recordingId}/complete ${res.status}: ${text}`,
    );
  }
}

/** POST /uploads/{id}/abort — отмена и cleanup в S3. Идемпотентен. */
export async function abortUpload(recordingId: string): Promise<void> {
  const res = await apiFetch(
    `/v1/uploads/${encodeURIComponent(recordingId)}/abort`,
    { method: "POST" },
  );
  if (!res.ok && res.status !== 404) {
    // 404 — запись уже удалена/abort'нута; считаем success.
    const text = await res.text().catch(() => "");
    throw new ApiError(
      res.status,
      `POST /uploads/${recordingId}/abort ${res.status}: ${text}`,
    );
  }
}
